import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface GoogleMapsProviderProps {
    children: ReactNode;
}

const SCRIPT_ID = 'google-maps-script-singleton';
const CALLBACK_NAME = '__minimalCrmGoogleMapsInit';

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        google?: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [CALLBACK_NAME]?: any;
    }
}

function isPlacesReady(): boolean {
    const places = typeof window !== 'undefined' ? window.google?.maps?.places : undefined;
    return !!(places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions && places?.AutocompleteSessionToken);
}

function getExistingScript(): HTMLScriptElement | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const byId = document.getElementById(SCRIPT_ID);
    if (byId && byId.tagName === 'SCRIPT') {
        return byId as HTMLScriptElement;
    }

    return document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
}

let loadPromise: Promise<void> | null = null;

function loadGoogleMapsPlaces(apiKey: string): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Google Maps can only be loaded in the browser.'));
    }

    if (!apiKey) {
        return Promise.reject(new Error('Missing Google Maps API key.'));
    }

    if (isPlacesReady()) {
        return Promise.resolve();
    }

    if (loadPromise) {
        return loadPromise;
    }

    loadPromise = new Promise<void>((resolve, reject) => {
        const existing = getExistingScript();

        window[CALLBACK_NAME] = () => {
            if (isPlacesReady()) {
                resolve();
            } else {
                reject(new Error('Google Maps script loaded but Places API is not available.'));
            }
        };

        const handleError = () => {
            reject(new Error('Failed to load Google Maps script.'));
        };

        if (existing) {
            existing.addEventListener('error', handleError, { once: true });
            // If it already loaded (but callback won’t fire again), resolve when ready.
            if (isPlacesReady()) {
                resolve();
            }
            return;
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.async = true;
        script.defer = true;
        script.addEventListener('error', handleError, { once: true });

        const params = new URLSearchParams({
            key: apiKey,
            v: 'weekly',
            libraries: 'places',
            loading: 'async',
            callback: CALLBACK_NAME,
        });

        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;

        document.head.appendChild(script);
    });

    return loadPromise;
}

const GoogleMapsContext = createContext<{ isLoaded: boolean; loadError: Error | null }>({
    isLoaded: false,
    loadError: null,
});

export function GoogleMapsProvider({ children }: GoogleMapsProviderProps) {
    const env = import.meta.env as unknown as Record<string, string | undefined>;
    const apiKey = env.VITE_GOOGLE_MAPS_API_KEY || env.GOOGLE_MAPS_API_KEY || '';

    const [isLoaded, setIsLoaded] = useState<boolean>(() => (typeof window !== 'undefined' ? isPlacesReady() : false));
    const [loadError, setLoadError] = useState<Error | null>(null);

    useEffect(() => {
        if (!apiKey) {
            return;
        }

        loadGoogleMapsPlaces(apiKey)
            .then(() => {
                setIsLoaded(true);
                setLoadError(null);
            })
            .catch((error) => {
                setIsLoaded(false);
                setLoadError(error instanceof Error ? error : new Error(String(error)));
            });
    }, [apiKey]);

    const ctxValue = useMemo(() => ({ isLoaded, loadError }), [isLoaded, loadError]);

    if (!apiKey) {
        return (
            <div className="text-destructive space-y-2 p-4 text-sm">
                <p className="font-medium">Missing Google Maps API key</p>
                <p className="text-xs">
                    Set <code className="bg-muted rounded px-1 py-0.5 font-mono">VITE_GOOGLE_MAPS_API_KEY</code> in your{' '}
                    <code className="bg-muted rounded px-1 py-0.5 font-mono">.env</code> file and restart the dev server.
                </p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="text-destructive border-destructive/50 bg-destructive/10 space-y-2 rounded-md border p-4 text-sm">
                <p className="font-medium">Error loading Google Maps</p>
                <p className="text-xs">{loadError.message || 'Unknown error'}</p>
            </div>
        );
    }

    if (!isLoaded) {
        return <div className="text-muted-foreground p-4 text-sm">Loading Google Maps...</div>;
    }

    return <GoogleMapsContext.Provider value={ctxValue}>{children}</GoogleMapsContext.Provider>;
}

export function useGoogleMaps() {
    return useContext(GoogleMapsContext);
}
